import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { PanelEdgeTab } from '@signals/app-shell/features/layout/PanelEdgeTab';
import { useFeatureFlagsStore } from '@signals/app-shell/stores/featureFlagsStore';
import MissionWorkspace from '@signals/app-shell/features/missions/MissionWorkspace';
import {
  Mission,
  MissionRouteStatus,
  activateNamedRoute,
  deleteNamedRoute,
  getMission,
  replaceMissionWaypoints,
  saveMissionRoute,
  saveMissionRouteIntel,
  saveNamedRoute,
} from '@signals/app-shell/services/missionApi';
import { PacketMapPolygon } from '@signals/app-shell/services/intelPacketApi';
import RoutePlannerLayers from '@signals/app-shell/features/routePlanner/map/RoutePlannerLayers';
import RoutePlannerPanel from '@signals/app-shell/features/routePlanner/panel/RoutePlannerPanel';
import RouteReferenceDock, { DockTabId } from '@signals/app-shell/features/routePlanner/dock/RouteReferenceDock';
import {
  persistablePayload,
  plannerIntel,
} from '@signals/app-shell/features/routePlanner/dock/intel';
import {
  alternatePayload,
  geometryKey,
  routeStats,
  withRouteName,
} from '@signals/app-shell/features/routePlanner/io/missionRoute';
import {
  isGenericWaypointLabel,
  sequentialWaypointLabel,
} from '@signals/app-shell/features/routePlanner/io/waypointLabel';
import { useMissionScan } from '@signals/app-shell/features/routePlanner/dock/useMissionScan';
import MissionAreasTab from '@signals/app-shell/features/missions/MissionAreasTab';
import MissionCotravelers from '@signals/app-shell/features/missions/MissionCotravelers';
import MissionCrewChat from '@signals/app-shell/features/missions/MissionCrewChat';
import { useMissionDockLive } from '@signals/app-shell/features/missions/useMissionDockLive';
import MissionChatToast from '@signals/app-shell/features/missions/MissionChatToast';
import MissionVehicleBar from '@signals/app-shell/features/missions/MissionVehicleBar';
import MissionVideoStrip from '@signals/app-shell/features/missions/MissionVideoStrip';
import MissionPhotosTab from '@signals/app-shell/features/missions/MissionPhotosTab';
import MissionDwellsTab from '@signals/app-shell/features/missions/MissionDwellsTab';
import MissionSignalsTab from '@signals/app-shell/features/missions/MissionSignalsTab';
import MissionReportTab from '@signals/app-shell/features/missions/MissionReportTab';
import MissionDeviceLayer from '@signals/app-shell/features/missions/map/MissionDeviceLayer';
import MissionThreatLayer from '@signals/app-shell/features/missions/map/MissionThreatLayer';
import MissionMediaLayer from '@signals/app-shell/features/missions/map/MissionMediaLayer';
import MissionDwellLayer from '@signals/app-shell/features/missions/map/MissionDwellLayer';
import MissionOptionPreviewLayer, {
  RouteOptionPreview,
} from '@signals/app-shell/features/missions/map/MissionOptionPreviewLayer';
import MissionRouteLayer from '@signals/app-shell/features/missions/map/MissionRouteLayer';
import MissionWaypointLayer from '@signals/app-shell/features/missions/map/MissionWaypointLayer';
import { pickTrailLead, reportedProgress } from '@signals/app-shell/features/missions/map/missionRouteTrail';
import { useNamedRouteEditor, waypointsForEditor } from '@signals/app-shell/features/missions/namedRouteEditor';
import { useMissionDeviceTracks } from '@signals/app-shell/features/missions/useMissionDeviceTracks';
import { useConvoyRedirect } from '@signals/app-shell/features/missions/convoyRedirect';
import {
  AreaDrawOverlay,
  AreaDrawPreview,
  useAreaDraw,
} from '@signals/app-shell/features/missions/AreaDrawTool';
import { criticalAlertCount } from '@signals/app-shell/features/missions/alertWaterfall';
import { watchlistLabels } from '@signals/app-shell/features/missions/areaPresence';
import {
  useRoutePlannerController,
  waypointFromCoords,
} from '@signals/app-shell/features/routePlanner/useRoutePlannerController';
import { decodePlannerParams } from '@signals/app-shell/features/routePlanner/io/permalink';
import IntelPacketMap, {
  INTEL_POINTS_BASE_LAYER,
  IntelMapLayer,
} from '../components/pathfinder/intel/IntelPacketMap';

const MissionsPage: React.FC = () => {
  const intelEnabled = useFeatureFlagsStore((s) => s.intel);
  const [center, setCenter] = useState<[number, number]>([27.9475, -82.4584]);
  const [zoom, setZoom] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [mission, setMission] = useState<Mission | null>(null);
  /** Tells the workspace its copy of the open mission is behind ours. */
  const [missionSignal, setMissionSignal] = useState(0);
  const [planning, setPlanning] = useState<Mission | null>(null);
  const [saving, setSaving] = useState(false);
  const [hoverPoint, setHoverPoint] = useState<{ longitude: number; latitude: number } | null>(null);
  const [runStatus, setRunStatus] = useState<MissionRouteStatus | null>(null);
  const [dockTabRequest, setDockTabRequest] = useState<{ id: DockTabId; key: number } | null>(null);
  const [optionPreview, setOptionPreview] = useState<RouteOptionPreview | null>(null);
  /** Which mission the camera was framed for, so a refresh is not a reselect. */
  const shownIdRef = useRef<number | null>(null);
  const mapRef = useRef<any>(null);
  const waypointPickRef = useRef<((longitude: number, latitude: number) => void) | null>(null);
  const [pickingWaypoint, setPickingWaypoint] = useState(false);
  const sharedState = useMemo(() => decodePlannerParams(window.location.search), []);
  const [scratchPlanning, setScratchPlanning] = useState(!!sharedState);

  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = Number(window.localStorage.getItem('missions-panel-width') || 0);
    return saved >= 320 && saved <= 760 ? saved : 420;
  });
  const [resizing, setResizing] = useState(false);
  const [panelOpen, setPanelOpen] = useState(
    () => window.localStorage.getItem('missions-panel-open') !== 'false',
  );
  const panelRef = useRef<HTMLDivElement | null>(null);
  const widthRef = useRef(panelWidth);
  widthRef.current = panelWidth;

  useEffect(() => {
    if (!resizing) return;
    const prevSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    const onMove = (e: MouseEvent) => {
      const left = panelRef.current?.getBoundingClientRect().left ?? 0;
      const next = Math.min(760, Math.max(320, e.clientX - left));
      widthRef.current = next;
      setPanelWidth(next);
    };
    const onUp = () => {
      setResizing(false);
      window.localStorage.setItem('missions-panel-width', String(widthRef.current));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      document.body.style.userSelect = prevSelect;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing]);

  useEffect(() => {
    window.localStorage.setItem('missions-panel-open', String(panelOpen));
  }, [panelOpen]);

  const plannerActive = !!planning || scratchPlanning;
  const named = useNamedRouteEditor(planning);

  const initialWaypoints = useMemo(() => {
    const liveStops = planning?.waypoints || sharedState?.waypoints || [];
    const source = waypointsForEditor(named.editingId, named.editing, liveStops);
    return source.map((w) => waypointFromCoords(
      w.longitude,
      w.latitude,
      isGenericWaypointLabel(w.label) ? undefined : (w.label || undefined),
      'mission',
      w.notes || undefined,
    ));
  }, [planning, named.editingId, named.editing, sharedState]);

  const exitPlanning = useCallback(() => {
    setPlanning(null);
    setScratchPlanning(false);
  }, []);

  const focusPoint = useCallback((longitude: number, latitude: number) => {
    setCenter([latitude, longitude]);
    setZoom(16);
    mapRef.current?.flyTo?.({ center: [longitude, latitude], zoom: 16, duration: 700 });
  }, []);

  const planner = useRoutePlannerController({
    active: plannerActive,
    sessionKey: planning
      ? `${planning.id}:${named.editingId}`
      : (scratchPlanning ? 'scratch' : null),
    onExit: exitPlanning,
    getMap: useCallback(() => mapRef.current, []),
    initialWaypoints,
    initialOptions: planning?.route_profile
      ? { profile: planning.route_profile as any }
      : sharedState?.options,
    initialPrimaryName: named.editing?.label
      || (typeof planning?.route_options?.route_name === 'string' ? planning.route_options.route_name : ''),
    initialAlternateName: named.editing?.alternate?.label || planning?.alt_route?.label || '',
    restoreAlternateKey: (named.editing?.alternate?.geometry || planning?.alt_route?.geometry)
      ? geometryKey(named.editing?.alternate?.geometry || planning?.alt_route?.geometry)
      : null,
    onError: setError,
  });

  const layers: IntelMapLayer[] = useMemo(() => {
    if (plannerActive) return [];
    // Stops are HTML markers (MissionWaypointLayer). Feeding them as GL
    // circles is what put them under the live route.
    return [];
  }, [mission, plannerActive]);

  // Watched areas stay visible while planning so a route can be drawn against them.
  const watchedAreas: PacketMapPolygon[] = useMemo(
    () => (mission?.geofences || [])
      .filter((f) => (f.geometry?.coordinates?.[0]?.length || 0) > 2)
      .map((f) => ({
        label: f.name,
        entityId: `geofence-${f.geofence_id}`,
        coordinates: f.geometry!.coordinates[0],
      })),
    [mission],
  );

  const handleMissionChange = useCallback((next: Mission | null) => {
    setMission(next);
    setRunStatus((current) => (current && next && current.events?.[0]?.mission_id === next.id
      ? current
      : null));
    // Only when the mission itself changes. This also runs when the workspace
    // re-reads the one it already had, and snapping the camera back to the
    // first stop every time somebody takes a reroute would throw away whatever
    // they were looking at — usually the part of the route in question.
    const nextId = next?.id ?? null;
    if (shownIdRef.current === nextId) return;
    shownIdRef.current = nextId;
    setOptionPreview(null);
    const first = next?.waypoints?.[0];
    if (first) {
      setCenter([first.latitude, first.longitude]);
      setZoom((z) => (z < 12 ? 13 : z));
    }
  }, []);

  const refreshMission = useCallback(async () => {
    const id = mission?.id;
    if (!id) return;
    try {
      setMission(await getMission(id));
      // The workspace on the other side of the map holds its own copy, so a
      // reroute taken from the dock would leave it quoting the old distance.
      setMissionSignal((n) => n + 1);
    } catch {
      // A failed refresh leaves the panel on the last good mission, which is a
      // better outcome than blanking it over a transient error.
    }
  }, [mission?.id]);

  // Which mission the dock is describing. While planning that is the one being
  // planned; otherwise it is whatever the workspace has selected.
  const dockMission = planning || mission;

  // Scanners bound to the saved mission, for when the planner is shut. They sit
  // disarmed until asked, so running alongside the planner's own costs nothing.
  const scan = useMissionScan(dockMission, {
    units: planner.state.options.units,
    onPersisted: refreshMission,
  });

  const intel = useMemo(() => (plannerActive
    ? plannerIntel(
      planner.state,
      { risk: planner.risk, assets: planner.assets, events: planner.events, weather: planner.weather },
      dockMission?.route_intel as any,
      dockMission?.route_intel_stale,
    )
    : scan.intel), [
    plannerActive, planner.state, planner.risk, planner.assets, planner.events, planner.weather,
    dockMission, scan.intel,
  ]);

  // Kept while planning as well: a route is drawn against where the convoy
  // actually is, and hiding the kit the moment an operator starts editing takes
  // away the one reference that makes a reroute make sense.
  const deviceTracks = useMissionDeviceTracks(dockMission, runStatus?.devices);
  const trailLead = useMemo(
    () => pickTrailLead(deviceTracks, runStatus?.progress?.lead_device_id),
    [deviceTracks, runStatus?.progress?.lead_device_id],
  );
  const convoyRedirect = useConvoyRedirect({
    mission: dockMission,
    status: runStatus,
    tracks: deviceTracks,
    onApplied: refreshMission,
  });

  const areaDraw = useAreaDraw(dockMission, { onSaved: refreshMission, onError: setError });

  const dockLive = useMissionDockLive({
    mission: dockMission,
    units: planner.state.options.units,
    onError: setError,
    onMissionChange: refreshMission,
    onStatus: setRunStatus,
    onFocus: focusPoint,
    onPreviewOption: setOptionPreview,
  });

  const openAlerts = useMemo(() => {
    if (!runStatus?.events) return null;
    return criticalAlertCount(runStatus.events, (id) => id);
  }, [runStatus]);

  const [pressingFollows, setPressingFollows] = useState<number | null>(null);
  const [areaCount, setAreaCount] = useState<number | null>(null);
  const [photoCount, setPhotoCount] = useState<number | null>(null);
  const [dwellCount, setDwellCount] = useState<number | null>(null);
  const [signalCount, setSignalCount] = useState<number | null>(null);
  const [reportCount, setReportCount] = useState<number | null>(null);

  useEffect(() => {
    setAreaCount(null);
    setPhotoCount(null);
    setDwellCount(null);
    setSignalCount(null);
    setReportCount(null);
  }, [dockMission?.id]);

  const savePlannedRoute = useCallback(async () => {
    if (!planning) return;
    const route = planner.primaryRoute;
    const stops = planner.state.waypoints.map((w, i) => ({
      label: sequentialWaypointLabel(w.label, i),
      latitude: w.latitude,
      longitude: w.longitude,
      notes: w.notes,
    }));
    const options = withRouteName({
      exclude: planner.state.options.exclude,
      depart_at: planner.state.options.departAt,
    }, planner.state.primaryName || named.editing?.label);
    const fallback = alternatePayload(
      planner.alternateRoute,
      planner.state.options.profile,
      options,
      planner.state.alternateName,
    );
    setSaving(true);
    setError(null);
    try {
      let refreshed;
      if (!named.editing || named.isLive) {
        await replaceMissionWaypoints(planning.id, stops);
        refreshed = await saveMissionRoute(planning.id, {
          profile: planner.state.options.profile,
          options,
          geometry: route?.geometry || null,
          stats: routeStats(route, planner.state.options.profile),
          alternate: fallback,
          clear_alternate: !fallback,
        });
        const findings = persistablePayload(intel);
        if (Object.keys(findings).length > 0) {
          try {
            await saveMissionRouteIntel(planning.id, findings as any);
          } catch {
            // The route itself is saved; losing the reference copy is a smaller
            // failure than telling the operator the save did not happen.
          }
        }
      } else {
        const previousIds = new Set((planning.saved_routes || []).map((item) => item.id));
        refreshed = await saveNamedRoute(planning.id, {
          id: named.editing.draft ? undefined : named.editing.id,
          label: planner.state.primaryName.trim() || named.editing.label,
          waypoints: stops,
          profile: planner.state.options.profile,
          options,
          geometry: route?.geometry || null,
          stats: routeStats(route, planner.state.options.profile),
          alternate: fallback,
          clear_alternate: !fallback,
        });
        if (named.editing.draft) {
          named.dropDraft(named.editing.id);
          const created = (refreshed.saved_routes || []).find((item) => !previousIds.has(item.id));
          if (created) named.setEditingId(created.id);
        }
      }

      planner.dispatch({ type: 'MARK_SAVED', savedRouteId: null });
      setMission(refreshed);
      setPlanning(refreshed);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not save the route');
    } finally {
      setSaving(false);
    }
  }, [planning, planner, intel, named]);

  if (!intelEnabled) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Missions are part of the Intel capability, which is turned off for this tenant.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      {!panelOpen && (
        <Box
          sx={{
            position: 'relative',
            width: 18,
            flexShrink: 0,
            height: '100%',
            display: { xs: 'none', md: 'block' },
            bgcolor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
          }}
        >
          <PanelEdgeTab side="right" title="Open missions" onClick={() => setPanelOpen(true)}>
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          </PanelEdgeTab>
        </Box>
      )}
      <Box
        ref={panelRef}
        sx={{
          position: 'relative',
          width: { xs: '100%', md: panelWidth },
          maxWidth: { xs: '100%', md: panelWidth },
          flexShrink: 0,
          height: '100%',
          minHeight: 0,
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: { xs: 'flex', md: panelOpen ? 'flex' : 'none' },
          flexDirection: 'column',
          overflow: 'visible',
        }}
      >
        <PanelEdgeTab side="right" title="Collapse" onClick={() => setPanelOpen(false)}>
          <ChevronLeftIcon sx={{ fontSize: 16 }} />
        </PanelEdgeTab>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          px: 1.5, pt: 1.25, pb: 1,
          background: 'linear-gradient(180deg, rgba(245,185,66,0.08) 0%, transparent 100%)',
        }}>
          {plannerActive ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                onClick={exitPlanning}
                sx={{ display: 'flex', cursor: 'pointer', color: 'text.secondary' }}
              >
                <ArrowBackIcon sx={{ fontSize: 16 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap sx={{ fontWeight: 800 }}>
                  Route planner
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {planning ? planning.name : 'Scratch route'} · Esc to exit
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: 0.15 }}>Missions</Typography>
                <Typography variant="caption" color="text.secondary">
                  Field tasks with a planned route and assigned phones
                </Typography>
              </Box>
              <Typography
                variant="caption"
                onClick={() => setScratchPlanning(true)}
                sx={{ fontWeight: 700, color: '#f5b942', cursor: 'pointer', pt: 0.5 }}
              >
                Scratch route
              </Typography>
            </Box>
          )}
        </Box>
        {error && <Typography variant="caption" color="error" sx={{ px: 1.5 }}>{error}</Typography>}
        <Divider />
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 1.5 }}>
          {plannerActive ? (
            <RoutePlannerPanel
              state={planner.state}
              dispatch={planner.dispatch}
              canUndo={planner.canUndo}
              canRedo={planner.canRedo}
              proximity={planner.proximity}
              missionId={planning?.id ?? null}
              routeName={planning?.name}
              saveLabel={named.isLive
                ? 'Save to mission'
                : `Save ${named.editing?.label || 'route'}`}
              saving={saving}
              onSave={planning ? savePlannedRoute : undefined}
              onRetry={planner.retry}
              onFocusWaypoint={planner.focusWaypoint}
              onFlyTo={planner.flyTo}
              namedSlots={named.slots}
              editingSlotId={named.editingId}
              canAddNamedSlot={named.canAdd}
              onSelectNamedSlot={(id) => {
                if (planner.state.dirty && id !== named.editingId) {
                  setError('Save this route before switching to another.');
                  return;
                }
                setError(null);
                named.setEditingId(id);
                const slot = named.slots.find((item) => item.id === id) || null;
                const stops = waypointsForEditor(id, slot, planning?.waypoints || []);
                planner.dispatch({
                  type: 'RESET',
                  waypoints: stops.map((w) => waypointFromCoords(
                    w.longitude,
                    w.latitude,
                    isGenericWaypointLabel(w.label) ? undefined : (w.label || undefined),
                    'mission',
                    w.notes || undefined,
                  )),
                  primaryName: slot?.label || '',
                  alternateName: slot?.alternate?.label || '',
                  restoreAlternateKey: slot?.alternate?.geometry
                    ? geometryKey(slot.alternate.geometry)
                    : null,
                });
              }}
              onAddNamedSlot={() => {
                if (planner.state.dirty) {
                  setError('Save this route before adding another.');
                  return;
                }
                setError(null);
                const slot = named.addSlot();
                planner.dispatch({
                  type: 'RESET',
                  waypoints: [],
                  primaryName: slot?.label || 'Secondary',
                  alternateName: '',
                  restoreAlternateKey: null,
                });
              }}
              onUseNamedSlot={async (id) => {
                if (!planning) return;
                if (planner.state.dirty) {
                  setError('Save this route before switching the convoy to it.');
                  return;
                }
                setSaving(true);
                setError(null);
                try {
                  const refreshed = await activateNamedRoute(planning.id, id);
                  setMission(refreshed);
                  setPlanning(refreshed);
                  named.setEditingId(id);
                } catch (e: any) {
                  setError(e?.response?.data?.detail || e?.message || 'Could not switch route');
                } finally {
                  setSaving(false);
                }
              }}
              onDeleteNamedSlot={async (id) => {
                if (!planning) return;
                const slot = named.slots.find((item) => item.id === id);
                if (slot?.draft) {
                  named.dropDraft(id);
                  return;
                }
                setSaving(true);
                setError(null);
                try {
                  const refreshed = await deleteNamedRoute(planning.id, id);
                  setMission(refreshed);
                  setPlanning(refreshed);
                } catch (e: any) {
                  setError(e?.response?.data?.detail || e?.message || 'Could not remove that route');
                } finally {
                  setSaving(false);
                }
              }}
            />
          ) : (
            <MissionWorkspace
              onError={setError}
              onWaypointsChange={handleMissionChange}
              refreshSignal={missionSignal}
              onPlanRoute={setPlanning}
              onFocusPoint={focusPoint}
              onWaypointPickChange={setPickingWaypoint}
              waypointPickRef={waypointPickRef}
              // The run lives in the dock's Sentinel / Activity tabs. Left
              // here as well it would poll twice and show two of every alert.
              hideRunStatus
              liveStatus={runStatus}
              onLiveStatus={setRunStatus}
              onEmergencyReroute={convoyRedirect.start}
              onCancelEmergencyReroute={convoyRedirect.cancel}
              emergencyReroutePicking={convoyRedirect.picking}
              emergencyRerouteBusy={convoyRedirect.busy}
              emergencyRerouteError={convoyRedirect.error}
              onOpenPhotos={() => setDockTabRequest({ id: 'photos', key: Date.now() })}
            />
          )}
        </Box>
        </Box>
      </Box>
      <Box
        onMouseDown={(e) => { e.preventDefault(); setResizing(true); }}
        sx={{
          display: { xs: 'none', md: panelOpen ? 'block' : 'none' },
          width: 6,
          cursor: 'col-resize',
          flexShrink: 0,
          bgcolor: resizing ? 'rgba(245,185,66,0.55)' : 'transparent',
          '&:hover': { bgcolor: 'rgba(245,185,66,0.35)' },
        }}
      />
      {/* A real flex basis, not `flex: 1`. With a basis of zero the map takes
          only what the panel and the dock leave it, so a wide panel beside a
          wide dock squeezes it to a strip; with one, the three give ground
          together and the minimum keeps the map plannable. */}
      <Box sx={{
        flex: '1 1 480px',
        minWidth: 280,
        display: { xs: 'none', md: 'block' },
        height: '100%',
        position: 'relative',
      }}>
        <IntelPacketMap
          center={center}
          zoom={zoom}
          layers={layers}
          geofences={watchedAreas}
          liveLayers={!!dockMission}
          captureClicks={plannerActive || pickingWaypoint || areaDraw.drawing || convoyRedirect.picking}
          cursor={plannerActive || pickingWaypoint || areaDraw.drawing || convoyRedirect.picking ? 'crosshair' : undefined}
          onMapClick={(ll) => {
            if (areaDraw.drawing) {
              areaDraw.addVertex(ll.lng, ll.lat);
              return;
            }
            if (convoyRedirect.picking) {
              void convoyRedirect.applyClick(ll.lng, ll.lat);
              return;
            }
            if (waypointPickRef.current) {
              waypointPickRef.current(ll.lng, ll.lat);
              return;
            }
            planner.handleMapClick(ll);
          }}
          onMapReady={(map) => { mapRef.current = map; }}
          onMove={(nextCenter, nextZoom) => {
            setCenter(nextCenter);
            setZoom(nextZoom);
          }}
        >
          {plannerActive && (
            <RoutePlannerLayers
              state={planner.state}
              legOffsets={planner.legOffsets}
              dispatch={planner.dispatch}
              hoverPoint={hoverPoint}
              // A held report scores positions along the route it was run on.
              // The panel can caveat that; the map cannot, so it gets nothing
              // rather than findings drawn in the wrong place.
              risk={planner.risk.stale ? null : planner.risk.report}
            />
          )}
          <MissionDeviceLayer
            tracks={deviceTracks}
            // While the planner owns the map every click places a stop, so the
            // pins step out of the way rather than eat one.
            interactive={!plannerActive && !pickingWaypoint && !areaDraw.drawing && !convoyRedirect.picking}
            units={planner.state.options.units}
            onSelect={(track) => focusPoint(track.longitude, track.latitude)}
          />
          {areaDraw.drawing && <AreaDrawPreview vertices={areaDraw.vertices} />}
          {dockMission && (
            <MissionThreatLayer
              missionId={dockMission.id}
              events={runStatus?.events}
              refreshKey={runStatus?.events?.length}
              interactive={!plannerActive && !pickingWaypoint && !convoyRedirect.picking}
              onSelect={(lat, lon) => focusPoint(lon, lat)}
              clearedEventIds={optionPreview?.focus?.clears?.map((c) => c.event_id)}
              exposedEventIds={optionPreview?.focus?.still_exposed?.map((c) => c.event_id)}
            />
          )}
          {!plannerActive && dockMission && (
            <MissionRouteLayer
              geometry={dockMission.route_geometry}
              alternate={dockMission.alt_route?.geometry}
              prior={dockMission.prior_route?.geometry}
              progress={reportedProgress(runStatus?.progress)}
              lead={trailLead}
              beforeId={INTEL_POINTS_BASE_LAYER}
            />
          )}
          {!plannerActive && dockMission && (
            <MissionWaypointLayer
              waypoints={dockMission.waypoints || []}
              interactive={!pickingWaypoint && !areaDraw.drawing && !convoyRedirect.picking}
              onSelect={(lat, lon) => focusPoint(lon, lat)}
            />
          )}
          <MissionOptionPreviewLayer preview={optionPreview} />
          {dockMission && (
            <MissionMediaLayer
              missionId={dockMission.id}
              refreshKey={runStatus?.events?.length}
              interactive={!plannerActive && !pickingWaypoint && !convoyRedirect.picking}
              onSelect={(lat, lon) => focusPoint(lon, lat)}
            />
          )}
          {dockMission && (
            <MissionDwellLayer
              missionId={dockMission.id}
              started={!!dockMission.started_at || dockMission.status === 'in_progress'}
              refreshKey={runStatus?.events?.length}
              interactive={!plannerActive && !pickingWaypoint && !convoyRedirect.picking}
              onSelect={(lat, lon) => focusPoint(lon, lat)}
            />
          )}
        </IntelPacketMap>
        {dockMission && !plannerActive && (
          <MissionVehicleBar
            missionId={dockMission.id}
            enabled={dockMission.status === 'in_progress' || dockMission.status === 'baseline'}
            onSent={setRunStatus}
          />
        )}
        {dockMission && (
          <MissionVideoStrip tracks={deviceTracks} onLocate={focusPoint} />
        )}
        {dockMission && <MissionChatToast />}
        <AreaDrawOverlay draw={areaDraw} />
      </Box>

      <RouteReferenceDock
        intel={intel}
        hasRoute={plannerActive ? !!planner.primaryRoute : !!dockMission?.route_geometry}
        hasDepartAt={plannerActive ? !!planner.state.options.departAt : scan.hasDepartAt}
        geometry={plannerActive
          ? planner.primaryRoute?.geometry || null
          : dockMission?.route_geometry || null}
        openAlerts={openAlerts}
        preferLive={dockMission?.status === 'in_progress'}
        requestTab={dockTabRequest?.id}
        requestTabKey={dockTabRequest?.key}
        banner={dockMission ? dockLive.banner : undefined}
        sentinel={dockMission ? dockLive.sentinel : undefined}
        activity={dockMission ? dockLive.activity : undefined}
        chat={dockMission ? <MissionCrewChat /> : undefined}
        areaCount={areaCount}
        areas={dockMission ? (
          <MissionAreasTab
            key={dockMission.id}
            mission={dockMission}
            deviceLabel={(id) => id}
            onFocus={focusPoint}
            onCount={setAreaCount}
            watchlist={watchlistLabels(runStatus?.events)}
            onDrawArea={areaDraw.start}
            onMissionChange={refreshMission}
          />
        ) : undefined}
        photoCount={photoCount}
        photos={dockMission ? (
          <MissionPhotosTab
            key={dockMission.id}
            missionId={dockMission.id}
            missionName={dockMission.name}
            refreshKey={runStatus?.events?.length}
            timeline={runStatus?.timeline}
            units={planner.state.options.units}
            canAlert={dockMission.status === 'in_progress' || dockMission.status === 'baseline'}
            onFocus={focusPoint}
            onCount={setPhotoCount}
            onAlerted={setRunStatus}
          />
        ) : undefined}
        dwellCount={dwellCount}
        dwells={dockMission ? (
          <MissionDwellsTab
            key={`dwells-${dockMission.id}`}
            missionId={dockMission.id}
            started={!!dockMission.started_at || dockMission.status === 'in_progress'}
            refreshKey={runStatus?.events?.length}
            onFocus={focusPoint}
            onCount={setDwellCount}
          />
        ) : undefined}
        reportCount={reportCount}
        report={dockMission ? (
          <MissionReportTab
            key={`report-${dockMission.id}`}
            mission={dockMission}
            onError={setError}
            onMissionChange={refreshMission}
            refreshKey={runStatus?.events?.length}
            onCount={setReportCount}
          />
        ) : undefined}
        pressingFollows={pressingFollows}
        follow={dockMission ? (
          <MissionCotravelers
            key={dockMission.id}
            missionId={dockMission.id}
            refreshKey={runStatus?.events?.length}
            onFocus={(lat, lon) => focusPoint(lon, lat)}
            onCountChange={setPressingFollows}
            units={planner.state.options.units}
            canAlert={dockMission.status === 'in_progress' || dockMission.status === 'baseline'}
            onAlerted={setRunStatus}
            events={runStatus?.events}
            timeline={runStatus?.timeline}
          />
        ) : undefined}
        signalCount={signalCount}
        signals={dockMission ? (
          <MissionSignalsTab
            key={`signals-${dockMission.id}`}
            missionId={dockMission.id}
            live={dockMission.status === 'in_progress' || dockMission.status === 'baseline'}
            hasAreas={dockMission.geofences.length > 0}
            onFocus={focusPoint}
            onCount={setSignalCount}
          />
        ) : undefined}
        onSelectRoute={plannerActive
          ? (index) => planner.dispatch({ type: 'SET_PRIMARY', index })
          : undefined}
        onSetAlternate={plannerActive
          ? (index) => planner.dispatch({ type: 'SET_ALTERNATE', index })
          : undefined}
        alternateIndex={planner.state.alternateIndex}
        primaryName={planner.state.primaryName}
        alternateName={planner.state.alternateName}
        onHoverRoute={plannerActive
          ? (index) => planner.dispatch({ type: 'HOVER_ROUTE', index })
          : undefined}
        onFlyToStep={plannerActive ? planner.flyToStep : undefined}
        onHoverElevation={plannerActive ? setHoverPoint : undefined}
        onFocusAsset={(asset) => focusPoint(asset.longitude, asset.latitude)}
        onFocusEvent={(event) => focusPoint(event.longitude, event.latitude)}
        onFocusAlert={(lat, lon) => focusPoint(lon, lat)}
        onFocusSegment={(segment) => focusPoint(segment.midpoint[0], segment.midpoint[1])}
        placesRadiusM={plannerActive ? planner.assets.radiusM : scan.placesRadiusM}
        onPlacesRadiusChange={plannerActive ? planner.assets.setRadiusM : scan.setPlacesRadiusM}
        eventsCorridorM={plannerActive ? planner.events.corridorM : scan.eventsCorridorM}
        onEventsCorridorChange={plannerActive ? planner.events.setCorridorM : scan.setEventsCorridorM}
        eventsHorizonHours={plannerActive ? planner.events.horizonHours : scan.eventsHorizonHours}
        onEventsHorizonChange={plannerActive ? planner.events.setHorizonHours : scan.setEventsHorizonHours}
        eventsWindowStart={plannerActive ? planner.events.windowStart : scan.eventsWindowStart}
        eventsWindowEnd={plannerActive ? planner.events.windowEnd : scan.eventsWindowEnd}
        onEventsWindowStartChange={plannerActive ? planner.events.setWindowStart : scan.setEventsWindowStart}
        onEventsWindowEndChange={plannerActive ? planner.events.setWindowEnd : scan.setEventsWindowEnd}
        corridorSettings={plannerActive ? planner.risk.settings : scan.corridorSettings}
        onCorridorSettingsChange={plannerActive ? planner.risk.setSettings : scan.setCorridorSettings}
      />
    </Box>
  );
};

export default MissionsPage;
